import { useCallback, useEffect, useMemo, useState } from "react";
import api, { euro, fmtDate, todayISO, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PayBadge } from "../components/StatusBadge";
import { ChevronLeft, ChevronRight, Printer, FileSpreadsheet, FileDown, LogIn, LogOut, Plus } from "lucide-react";
import { addDays } from "date-fns";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Agenda() {
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [appts, setAppts] = useState([]);
  const [physiosPresent, setPhysiosPresent] = useState([]);
  const [medicalBatch, setMedicalBatch] = useState([]);

  const load = useCallback(async () => {
    const [appointmentsResult, dashboardResult, medicalResult] = await Promise.allSettled([
      api.get("/appointments", { params: { date_from: date, date_to: date } }),
      api.get("/dashboard", { params: { date } }),
      api.get("/medical-availabilities/batch", { params: { date_from: date, date_to: date } }),
    ]);
    if (appointmentsResult.status === "fulfilled") setAppts(appointmentsResult.value.data);
    if (dashboardResult.status === "fulfilled") setPhysiosPresent(dashboardResult.value.data.physios_present || []);
    if (medicalResult.status === "fulfilled") setMedicalBatch(medicalResult.value.data.availabilities || []);
    else setMedicalBatch([]);
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const nav = (dir) => setDate(iso(addDays(new Date(date + "T12:00:00"), dir)));

  const doctorStats = useMemo(() => {
    const grouped = new Map();
    medicalBatch.forEach((availability) => {
      const id = availability.physio_id;
      if (!grouped.has(id)) grouped.set(id, {
        doctor: availability.doctor,
        total: 0,
        free: 0,
        occupied: 0,
      });
      const row = grouped.get(id);
      row.total += availability.grid.total;
      row.free += availability.grid.free;
      row.occupied += availability.grid.occupied;
    });
    grouped.forEach((row, id) => {
      const docs = appts.filter((a) => a.physio_id === id);
      row.completed = docs.filter((a) => a.status === "completato").length;
      row.cancelled = docs.filter((a) => a.status === "annullato").length;
      row.noShow = docs.filter((a) => a.status === "non_presentato").length;
      row.toDo = docs.filter((a) => ["prenotato", "confermato", "arrivato", "in_trattamento"].includes(a.status)).length;
      row.occupancy = row.total ? Math.round((row.occupied / row.total) * 100) : 0;
    });
    return Array.from(grouped.values());
  }, [medicalBatch, appts]);

  const exportCSV = () => {
    const rows = [["Ora", "Paziente", "Trattamento", "Professionista", "Tipo", "Macchinario", "Note", "Stato", "Pagamento", "Importo"]];
    appts.forEach((a) => rows.push([`${a.start}-${a.end}`, a.patient_name, a.treatment_name, a.physio_name, a.booking_mode === "medical_slot" ? "Medico" : "Fisioterapia", a.machine_name, a.notes || "", a.status, a.payment_status, a.price]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `agenda_${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Agenda esportata in formato Excel (CSV)");
  };

  const checkio = async (a, kind) => {
    try {
      await api.post(`/appointments/${a.id}/${kind}`);
      toast.success(kind === "checkin" ? `Check-in ${a.patient_name}` : `Check-out ${a.patient_name}`);
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4" data-testid="agenda-page">
      <div className="flex flex-wrap items-center gap-2 no-print">
        <h1 className="font-heading text-2xl font-bold">Agenda del giorno</h1>
        <div className="flex items-center gap-1 ml-auto">
          <button data-testid="agenda-prev" onClick={() => nav(-1)} className="p-2 rounded-xl bg-white border border-slate-200"><ChevronLeft size={16} /></button>
          <input data-testid="agenda-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm bg-white" />
          <button data-testid="agenda-next" onClick={() => nav(1)} className="p-2 rounded-xl bg-white border border-slate-200"><ChevronRight size={16} /></button>
        </div>
        {can(user, "agenda", "export") && <>
          <button data-testid="agenda-print" onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm hover:border-blue-300"><Printer size={15} /> Stampa</button>
          <button data-testid="agenda-export-csv" onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm hover:border-emerald-300"><FileSpreadsheet size={15} /> Excel</button>
          <button data-testid="agenda-export-pdf" onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm hover:border-red-300"><FileDown size={15} /> PDF</button>
        </>}
        {can(user, "calendario", "create") && <button data-testid="agenda-new" onClick={() => openModal("appointment", { date })} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium"><Plus size={15} /> Fisioterapia</button>}
      </div>

      {doctorStats.length > 0 && (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-3 no-print" data-testid="agenda-doctor-stats">
          {doctorStats.map((row) => (
            <div key={row.doctor.id} className="bg-white rounded-2xl border p-4" style={{ borderColor: `${row.doctor.color}55` }}>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: row.doctor.color }} />
                <div className="min-w-0">
                  <p className="font-heading font-bold truncate">{row.doctor.first_name} {row.doctor.last_name}</p>
                  <p className="text-xs text-slate-400 truncate">{row.doctor.specialization || "Specialista"}</p>
                </div>
                <span className="ml-auto text-sm font-semibold" style={{ color: row.doctor.color }}>{row.occupancy}%</span>
              </div>
              <div className="grid grid-cols-4 gap-1.5 mt-3 text-center">
                <div className="rounded-xl bg-slate-50 px-1 py-2"><p className="text-lg font-bold">{row.total}</p><p className="text-[9px] uppercase text-slate-400">Totali</p></div>
                <div className="rounded-xl bg-emerald-50 px-1 py-2"><p className="text-lg font-bold text-emerald-700">{row.free}</p><p className="text-[9px] uppercase text-emerald-600">Liberi</p></div>
                <div className="rounded-xl bg-blue-50 px-1 py-2"><p className="text-lg font-bold text-blue-700">{row.occupied}</p><p className="text-[9px] uppercase text-blue-600">Prenotati</p></div>
                <div className="rounded-xl bg-amber-50 px-1 py-2"><p className="text-lg font-bold text-amber-700">{row.toDo}</p><p className="text-[9px] uppercase text-amber-600">Da fare</p></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-500">
                <span>Completati <b>{row.completed}</b></span>
                <span>Annullati <b>{row.cancelled}</b></span>
                <span>No-show <b>{row.noShow}</b></span>
                <span>Occupazione <b>{row.occupancy}%</b></span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div id="agenda-print" className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="agenda-table-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <h2 className="font-heading text-xl font-bold capitalize">{new Date(date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h2>
            <p className="text-sm text-slate-500">Fisioterapisti in turno: {physiosPresent.length ? physiosPresent.map((p) => p.name).join(", ") : "—"}</p>
            {doctorStats.length > 0 && <p className="text-sm text-slate-500">Medici in ambulatorio: {doctorStats.map((s) => `${s.doctor.first_name} ${s.doctor.last_name}`).join(", ")}</p>}
            {doctorStats.length > 0 && (
              <div className="hidden print:flex print-summary-line">
                {doctorStats.map((row) => (
                  <span key={row.doctor.id} className="print-summary-doctor">
                    <b>{row.doctor.first_name} {row.doctor.last_name}</b>
                    <span>Totali <b>{row.total}</b></span>
                    <span>Liberi <b>{row.free}</b></span>
                    <span>Prenotati <b>{row.occupied}</b></span>
                    <span>Da fare <b>{row.toDo}</b></span>
                    <span>Occupazione <b>{row.occupancy}%</b></span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-400 no-print">{appts.length} appuntamenti</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="agenda-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-2 print-col-hour">Ora</th><th className="pr-2 print-col-patient">Paziente</th><th className="pr-2 print-hide-col">Trattamento</th>
                <th className="pr-2 print-col-professional">Professionista</th><th className="pr-2 print-hide-col">Macchinario</th><th className="pr-2 print-col-notes">Note</th>
                <th className="pr-3 print-hide-col">Stato</th><th className="pr-3 print-hide-col">Pagamento</th><th className="no-print"></th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} data-testid={`agenda-row-${a.id}`} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => openModal("appointment", { appointment: a })}>
                  <td className="py-2.5 pr-2 font-mono font-semibold whitespace-nowrap print-col-hour">{a.start}<span className="text-slate-300 font-normal">–{a.end}</span></td>
                  <td className="pr-2 font-medium print-col-patient">{a.patient_name}</td>
                  <td className="pr-2 text-slate-600 print-hide-col">{a.treatment_name || "—"}</td>
                  <td className="pr-2 print-col-professional">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: a.physio_color }} />{a.physio_name}</span>
                    {a.booking_mode === "medical_slot" && <span className="text-[9px] uppercase tracking-wide text-slate-400 no-print">medico · slot</span>}
                  </td>
                  <td className="pr-2 text-slate-600 print-hide-col">{a.machine_name || "—"}</td>
                  <td className="pr-2 text-slate-500 text-xs max-w-56 print-col-notes">
                    <div className="relative group/note">
                      <span className="block truncate cursor-help">{a.notes || "—"}</span>
                      {a.notes && (
                        <div className="hidden group-hover/note:block absolute z-50 left-0 top-full mt-2 w-80 max-w-[70vw] rounded-xl border border-violet-300 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 shadow-xl whitespace-normal">
                          {a.notes}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="pr-3 print-hide-col"><StatusBadge status={a.status} /></td>
                  <td className="pr-3 whitespace-nowrap print-hide-col"><PayBadge status={a.payment_status} /> <span className="text-xs text-slate-500">{euro(a.price)}</span></td>
                  <td className="no-print whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {can(user, "checkin", "create") && !a.checkin_at && !["completato", "annullato"].includes(a.status) && (
                      <button data-testid={`agenda-checkin-${a.id}`} onClick={() => checkio(a, "checkin")} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 mr-1" title="Check-in"><LogIn size={15} /></button>
                    )}
                    {can(user, "checkin", "create") && a.checkin_at && !a.checkout_at && (
                      <button data-testid={`agenda-checkout-${a.id}`} onClick={() => checkio(a, "checkout")} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" title="Check-out"><LogOut size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {appts.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400" data-testid="agenda-empty">Nessun appuntamento per questa giornata</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-300 mt-6 hidden print:block">LanuseiLAB — Software realizzato da Multiprojects.it · Maurizio Deplano</p>
      </div>
    </div>
  );
}
